import { Injectable } from '@angular/core';
import { concat, forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap, toArray } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppService } from '../shared/app.service';
import { ImageProcessingService } from '../shared/image-processing.service';
import { SignatureInfo } from '../shared/signature-info';
import { ClientApi, FileResponse } from '../client-api.service';

@Injectable({
  providedIn: 'root'
})
export class SignatureRendererService {
  /* eslint-disable max-len */
  private readonly bodyTemplate =
    `<div style="display:block"><table border="0" cellpadding="1" cellspacing="0" width="380" class="vcard" style="background-color: #fff; border-collapse: collapse; border: none; color: #000; background-color: #fff; font-family: Calibri,sans-serif; font-size: 12pt">
<tr>
<td><a href="{{orgWebsiteUrl}}"><img style="border:none; width:{{orgLogoWidth}}px" border="0" src="{{orgLogoUrl}}" alt="{{orgName}} logo"/></a></td>
<td></td>
<td align="right"><img style="border:none;width:{{anniversaryWidth}}px; height:{{anniversaryHeight}}px" border="0" width="{{anniversaryWidth}}" height="{{anniversaryHeight}}" src="{{anniversaryUrl}}" alt="Celebrating 50+ Years!"/></td>
</tr>
<tr>
<td colspan="3" valign="bottom" style="font-size: 16pt" class="fn"><b><span style="color:#968159">{{personNameFirst}} {{personNameRemaining}}</span></b></td>
</tr>
<tr>
<td colspan="3" valign="bottom" style="font-size: 14pt;margin-bottom:5px;" class="fn"><b><span style="color:#968159">{{title}}</span></b></td>
</tr>
<tr><td colspan="3"><span style="color:#3f5465;font-size:14px;">Phone:</span> <a style="color: #3f5465; text-decoration:none; text-underline:none;" vlink="#3f5465" href="{{phoneLink}}"><span class="tel" style="color: #3f5465; text-decoration:none; text-underline:none;font-size:14px;">{{phone}}</span></a></td></tr>
<tr><td colspan="3" class="adr"><span style="color:#3f5465;font-size:14px;">{{orgAddress}}</span></td></tr>
<tr><td colspan="3" style="margin-top:5px;margin-bottom:5px;"><img style="border:none;width:{{tagLineWidth}}px; height:{{tagLineHeight}}px;" border="0" src="{{tagLineImageUrl}}" width="{{tagLineWidth}}" height="{{tagLineHeight}}" alt="Making home the heart of business." /></td></tr>
<tr><td colspan="3" style="margin-top:5px;margin-bottom:5px;">{{socialLinksRendered}}</td></tr>
</table>
</div>
<div style="display:block;padding-top:5px;">
<table border="0" cellpadding="1" cellspacing="0" width="374" class="vcard" style="background-color: #fff; border-collapse: collapse; border: none; color: #000; background-color: #fff; font-family: Calibri,sans-serif; font-size: 12pt">
<tr>
<td style="width:65px;display:{{showGNVBestTableCell}}" valign="middle">
  <img style="border:none;width:{{bestPlacesToWorkWidth}}px; height:{{bestPlacesToWorkHeight}}px;margin-top:5px;margin-bottom:3px;display:{{showGNVBest}}" border="0" src="{{bestPlacesToWorkUrl}}" width="{{bestPlacesToWorkWidth}}" height="{{bestPlacesToWorkHeight}}" alt="Best Places to work in Gainesville!" />
</td>
<td style="width:65px" valign="middle">
  <img style="border:none;width:{{oraSealWidth}}px; height:{{oraSealHeight}}px;margin-top:5px;margin-bottom:3px;" border="0" src="{{oraSealUrl}}" width="{{oraSealWidth}}" height="{{oraSealHeight}}" alt="Ora Seal." />
</td>
<td valign="middle" style="margin-right:15px;">
  <img style="border:none;width:{{floridaTrendWidth}}px; height:{{floridaTrendHeight}}px;margin-top:5px;margin-bottom:3px;" border="0" src="{{floridaTrendUrl}}" width="{{floridaTrendWidth}}" height="{{floridaTrendHeight}}" alt="Florida trend Private companies." />
</td>
<td valign="middle">
  <a href="https://warrington.ufl.edu/master-of-science-in-real-estate"><img style="border:none;width:{{msrealestateWidth}}px; height:{{msrealestateHeight}}px;margin-top:5px;margin-bottom:3px;display:{{showMSRE}}" border="0" src="{{msrealestateUrl}}" width="{{msrealestateWidth}}" height="{{msrealestateHeight}}" alt="Florida trend Private companies." /></a>
</td>
</tr>
</table>
<div style="margin-top: 15px;" >
  <a href="https://colliercompanies.com/join-our-team/"><img style="border:none;width:{{joinourteamWidth}}px; height:{{joinourteamHeight}}px;margin-top:5px;margin-bottom:3px;" border="0" src="{{joinourteamUrl}}" width="{{joinourteamWidth}}" height="{{joinourteamHeight}}" alt="Florida trend Private companies." /></a>
</div>
</div>
`;

  private readonly socialLinkTemplateItemSplit = '&nbsp;&nbsp';
  private readonly socialLinkTemplate =
    `<a href="{{url}}" style="color:#968159;text-decoration:none;font-weight:700;font-size:14px">{{social}}</a>`;


  private readonly orgMap$ = this.appSvc.orgs$.pipe(
    map((orgs) => {
      const withDataUrls = orgs.map(x => {
        //const logoUrl = this.combineUrlParts(environment.logosBaseUrl, `${x.orgCode?.toUpperCase()}.png`);
        return { ...x, logoDataUrl$: this.getLogoImage$(x.orgCode!).pipe(shareReplay(1)) };
      });
      const orgMap = new Map(withDataUrls.map(x => [x.orgCode, x]));
      return orgMap;
    }),
    shareReplay(1)
  );

  private readonly socMedSvcs$ = this.appSvc.socialMediaServices$.pipe(
    map((socMedSvcs) => socMedSvcs.map(x => {
      const socialLogoUrl = this.combineUrlParts(environment.socialLogosBaseUrl, x.iconUrl);      
      return { ...x, logoDataUrl$: this.getImage$(socialLogoUrl, 30, 30).pipe(shareReplay(1)) };
    })),
    shareReplay(1)
  );

  private readonly tagLineDataUrl$ = this.getImage$(environment.taglineUrl).pipe(shareReplay(1));
  private readonly anniversaryDataUrl$ = this.getImage$(environment.anniversaryUrl).pipe(shareReplay(1));
  private readonly floridaTrendDataUrl$ = this.getImage$(environment.floridaTrendUrl).pipe(shareReplay(1));
  private readonly msrealestateDataUrl$ = this.getImage$(environment.msrealestateUrl).pipe(shareReplay(1));
  private readonly joinourteamUrlDataUrl$ = this.getImage$(environment.joinourteamUrl).pipe(shareReplay(1));
  private readonly oraSealDataUrl$ = this.getImage$(environment.oraSealUrl).pipe(shareReplay(1));
  private readonly bestPlacesToWorkUrl$ = this.getImage$(environment.bestPlacesToWorkUrl).pipe(shareReplay(1));

  /* eslint-enable max-len */
  constructor(private appSvc: AppService, 
              private clientApi: ClientApi, 
              private imgProcSvc: ImageProcessingService) { }

  renderSignatureInfo(sig: SignatureInfo | undefined) {
    if (!sig) {
      // make an empty element;
      return of('');
    }

    return forkJoin([
      this.orgMap$.pipe(map(x => x.get(sig.orgCode))),
      this.appSvc.orgSocLinksMap$
    ]).pipe(
      switchMap(([org, socLinksMap]) => forkJoin({
        org: of(org),
        orgLogoInfo: org?.logoDataUrl$ ?? of(null),
        socialLinksRendered: this.renderSocialLinks(socLinksMap.get(org?.orgCode || environment.defaultOrgCode)),
        tagLineImageInfo: this.tagLineDataUrl$ ?? of(null),
        floridaTrendImageInfo: this.floridaTrendDataUrl$ ?? of(null),
        anniversaryImageInfo: this.anniversaryDataUrl$ ?? of(null),
        msrealestateImageInfo: this.msrealestateDataUrl$ ?? of(null),
        joinourteamImageInfo: this.joinourteamUrlDataUrl$ ?? of(null),
        oraSealImageInfo: this.oraSealDataUrl$ ?? of(null),
        bestPlacesToWorkInfo: this.bestPlacesToWorkUrl$ ?? of(null)
      })),
      // eslint-disable-next-line max-len
      map(({ org, orgLogoInfo, socialLinksRendered, tagLineImageInfo, floridaTrendImageInfo, anniversaryImageInfo, joinourteamImageInfo, msrealestateImageInfo, oraSealImageInfo, bestPlacesToWorkInfo }) => {
        const personNameFirst = /^\s*\S+(?=\s)/.exec(sig.personName)?.[0].trim() ?? sig.personName.trim();
        const personNameRemaining = /\s+.*?$/.exec(sig.personName)?.[0].trim() ?? '';
        const bodyData = {
          ...sig,
          orgAddress: org?.fullAddress || '',
          orgLogoHeight: 50, // not used currently, but could be if we want to constrain logo height instead of width
          orgLogoWidth: 140,
          orgLogoUrl: orgLogoInfo, //?.url,
          orgName: org?.name,
          orgWebsiteUrl: org?.websiteUrl,
          personNameFirst,
          personNameRemaining,
          socialLinksRendered,
          tagLineHeight: tagLineImageInfo.height,
          tagLineWidth: tagLineImageInfo.width,
          tagLineImageUrl: tagLineImageInfo.url,
          floridaTrendHeight: floridaTrendImageInfo.height,
          floridaTrendWidth: floridaTrendImageInfo.width,
          floridaTrendUrl: floridaTrendImageInfo.url,
          anniversaryHeight: anniversaryImageInfo.height,
          anniversaryWidth: anniversaryImageInfo.width,
          anniversaryUrl: anniversaryImageInfo.url,
          msrealestateHeight: msrealestateImageInfo.height,
          msrealestateWidth: msrealestateImageInfo.width,
          msrealestateUrl: msrealestateImageInfo.url,
          joinourteamUrl: joinourteamImageInfo.url,
          joinourteamHeight: joinourteamImageInfo.height,
          joinourteamWidth: joinourteamImageInfo.width,
          bestPlacesToWorkHeight: bestPlacesToWorkInfo.height,
          bestPlacesToWorkWidth: bestPlacesToWorkInfo.width,
          bestPlacesToWorkUrl: bestPlacesToWorkInfo.url,
          oraSealHeight: oraSealImageInfo.height,
          oraSealWidth: oraSealImageInfo.width,
          oraSealUrl: oraSealImageInfo.url,
          phoneLink: this.makeTelLink(sig.phone),
          showMSRE: (org?.name === 'The Collier Companies' ? 'block' : 'none'),
          showGNVBest: ((org?.fullAddress?.includes('Gainesville') || org?.fullAddress?.includes('Newberry')) ? 'block' : 'none'),
          // eslint-disable-next-line max-len
          showGNVBestTableCell: ((org?.fullAddress?.includes('Gainesville') || org?.fullAddress?.includes('Newberry')) ? 'table-cell' : 'none'),
        };
        return this.renderTemplate(this.bodyTemplate, bodyData);
      })
    );
  }

  private combineUrlParts(...urlParts: (string | undefined)[]) {
    return urlParts.map(x => x?.trim()).filter(x => !!x).join('/').replace(/\/{2,}/g, '/');
  }
  
  private getImage$(url: string, maxWidth?: number, maxHeight?: number) {
    if (maxWidth || maxHeight) {
      return this.imgProcSvc.resizeImage(url, { backgroundColor: '#fff', maxWidth, maxHeight })
        .pipe(map(x => ({ width: x.width, height: x.height, url: x.toDataURL('image/jpeg', 1) })));
    }
    else {
      return forkJoin([this.imgProcSvc.toImage(url), this.imgProcSvc.toDataUrl(url, 'image/jpeg', 1)])
        .pipe(map(([img, dataUrl]) => ({ height: img.height, width: img.width, url: dataUrl}) ));
    }
  }
  
  private getLogoImage$(orgCode: string): Observable<string> {        
    return this.clientApi.getImageFileByOrgcode(orgCode).pipe(
      switchMap(res => {
        const img = res.result;
        const url = this.imgProcSvc.blobToDataUrl(img.data);        
        return (url);
      })      
    );
  }

  private makeTelLink(phone: string) {
    return `tel:${phone.replace(/\W+/g, '').replace(/\D+/g, 'p').trim()}`;
  }

  private renderSocialLinks(socialUrls: { [key: string]: string } | undefined) {
    return this.socMedSvcs$.pipe(
      switchMap(x => concat(...x.map(y => y.logoDataUrl$.pipe(map(z => ({ ...y, logoInfo: z })))))),
      toArray(),
      map(socMedSvcs => socMedSvcs
        .map(x => ({
          social: (x.applicationKey == "Collier" ? 'CollierCompanies.com' : x.applicationKey),
          alt: `${x.applicationKey} logo`,
          iconHeight: x.logoInfo.height,
          iconWidth: x.logoInfo.width,
          iconUrl: x.logoInfo.url,
          url: socialUrls?.[x.applicationKey || '']
        }))
        .filter(x => !!x.url)
        .map(x => this.renderTemplate(this.socialLinkTemplate, x))
        .join(this.socialLinkTemplateItemSplit)
      )
    );
  }
  private renderTemplate(template: string, src: { [key: string]: any }) {
    let output = template;
    Object.entries(src)
      .forEach(([k, v]) => output = output.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v));
    return output;
  }
}
